import React from 'react';
import cn from 'classname';
import uniq from 'lodash/uniq';
import find from 'lodash/find';
import some from 'lodash/some';
import flatten from 'lodash/flatten';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import upperFirst from 'lodash/upperFirst';

import pluralize from '../../../shared/js/pluralize';
import { getCurrencyIcon } from '../../../shared/js/get-currency-icon';
import geoLandingLangRelation from '../../../shared/js/geo_landing_lang_relations';
import Logger from '../shared/logger';

const log = new Logger({
	name: 'offer-geo-matching-error',
	message: "For offer '%s' with id %s missing geo relation %s"
});

const isRocket = process.env.CPA_ENV === 'rocket';

export const calculateGeoGoals = (offer, useCountryCode) => {
	const goals = [];

	offer.locations &&
	offer.locations.forEach(location => {
		offer.payouts && offer.payouts.forEach(payout => {
			let matchedPayout = find(offer.base_payouts, {location_id: payout.location_id, currency: payout.currency});
			if (matchedPayout) {
				if (matchedPayout.amount != payout.amount) {
					payout.isDifferentFromBasePayout = true;
					payout.differenceBetweenBaseAndIndividual = Number(payout.amount) - Number(matchedPayout.amount);
				}
			}
			if (payout.location_id === location.id) {
				let sameLocation = find(goals, { locationId: location.id });
				if (sameLocation) {
					sameLocation.payouts = sameLocation.payouts.concat({
						payout: payout.amount,
						payoutCurrency: payout.currency,
						isDifferentFromBasePayout: payout.isDifferentFromBasePayout,
						differenceBetweenBaseAndIndividual: payout.differenceBetweenBaseAndIndividual
					});
				} else {
					goals.push({
						locationId: location.id,
						location_restrictions: location.location_restrictions,
						locationTitle: useCountryCode ? location.name.length && location.name.length <= 14
							? location.name : location.country_code : location.name,
						locationCountryCode: location.country_code,
						isOwnCallcenter: location.is_own_callcenter,
						isContestEnabled: location.is_contest_enabled,
						isBoosterAvailable: location.is_booster_available,
						contestPoints: location.contest_points,
						price: location.price,
						priceCurrency: location.currency,
						boost_till: payout.boost_till,
						boost_value: location.boost_value,
						boost_info: offer.boost_info,
						payouts: [
							{
								payout: payout.amount,
								payoutCurrency: payout.currency,
								isDifferentFromBasePayout: payout.isDifferentFromBasePayout,
								differenceBetweenBaseAndIndividual: payout.differenceBetweenBaseAndIndividual
							}
						]
					});
				}
			}
		});
	});
	offer.payouts && offer.payouts.forEach(payout => {
		if (payout.is_individual_term) {
			find(goals, {'locationId': payout.location_id}).is_individual_term = true;
		}
	});
	offer.goals = goals && goals.sort(
		(prev, current) =>  (prev.locationTitle > current.locationTitle) ? 1 : -1
	);
	offer.goals = goals;
	return offer;
};

export const translateCategory = (category, lists) => {
	const found =  find(lists['offer-categories'], { id: category });

	return {
		...found,
		text: upperFirst(found.text)
	};
};

export const getUniqPayoutCurrencies = goals => {
	const currencies = [];
	goals &&
	goals.forEach(
		goal =>
			goal.payouts &&
		goal.payouts.forEach(payout => {
			currencies.push(payout.payoutCurrency);
		})
	);
	return uniq(currencies).sort((a, b) => {
		return b == 'usd' ? 1 : 0;
	});
};

export const getPayoutHeaders = (currencies, intl, className) => {
	if (currencies && currencies.length > 0) {
		return currencies.filter(c => c !== 'rub').map((c, i) => (
			<th className={className} key={i} width={1}><span>{intl('Выпл.')}{' '}{getCurrencyIcon(c)}</span></th>
		));
	} else {
		return <th className={className} width={1}><span>{intl('Выпл.')}</span></th>;
	}
};

export const getPayoutCells = (payouts, currencies, intl, className, boostTill = null, boost_value) => {
	return (
		currencies &&
		currencies.length > 0 &&
		currencies.filter(c => c !== 'rub').map((c, i) => {
			let p = find(payouts, { payoutCurrency: c });
			if (p) {
				const isBoostedOrFromBasePayout = p.isDifferentFromBasePayout || !isNull(boostTill);
				if (!isNull(boostTill)) {
					const basePayout = Math.round(p.payout / (1 + Number(boost_value)));
					p.differenceBetweenBaseAndIndividual = p.payout - basePayout;
				}
				return (
					<td className={cn(className, isBoostedOrFromBasePayout && 'different-from-base')} key={i} width={1}>
						<span className="money">
							{p.payout} <span className="currency">{getCurrencyIcon(p.payoutCurrency)}</span>
						</span>
						{isBoostedOrFromBasePayout &&
						<span className={cn('difference-amount', !isNull(boostTill) && 'boosted')}>
							<span className="text-grey">{intl('Спец. условия')}</span>{' '}
							<span className="text-black">
								{p.differenceBetweenBaseAndIndividual > 0 ? '+' : '-'}{' '}
								{Math.abs(p.differenceBetweenBaseAndIndividual)}{' '}{getCurrencyIcon(p.payoutCurrency)}</span></span>}
					</td>
				);
			} else {
				return <td className={className + ' no-payout'} key={i} width={1}>—</td>;
			}
		})
	);
};

export const getTrafficTypes = intl => {
	return !isRocket ? [
		{
			text: intl('Веб-сайты')
		},
		{
			text: intl('Дорвеи')
		},
		{
			text: intl('Контекстная реклама')
		},
		{
			text: intl('Контекстная реклама на бренд')
		},
		{
			text: intl('Тизерная реклама')
		},
		{
			text: intl('Facebook: таргетированная реклама')
		},
		{
			text: intl('Facebook: паблики, игры, приложения')
		},
		{
			text: intl('Email-рассылка')
		},
		{
			text: 'CashBack',
			isNotAllowed: true
		},
		{
			text: 'ClickUnder/PopUnder',
			isNotAllowed: true
		},
		{
			text: intl('Брокеры'),
			isNotAllowed: true
		},
		{
			text: intl('Мотивированный'),
			isNotAllowed: true
		},
		{
			text: intl('Баннерная реклама')
		},
		{
			text: intl('Ретаргетинг')
		}
	] : [
		{
			text: 'Teasers/Native'
		},
		{
			text: 'Facebook Ads'
		},
		{
			text: 'Google Ads'
		},
		{
			text: 'News Feed'
		},
		{
			text: 'SEO'
		},
		{
			text: 'Banner'
		},
		{
			text: 'Tik Tok',
			isNotAllowed: true
		},
		{
			text: 'Push',
			isNotAllowed: true
		},
		{
			text: 'ClickUnder/PopUnder',
			isNotAllowed: true
		},
		{
			text: 'Motivated',
			isNotAllowed: true
		},
		{
			text: 'Spam',
			isNotAllowed: true
		},
		{
			text: 'Email',
			isNotAllowed: true
		},
		{
			text: 'Doorway',
			isNotAllowed: true
		}
	];
};

export const getOfferLangs = (offer, landingLanguagesList, intl) => {
	const availableLangs = getLangsByTargetRelation(offer);

	const offerLangs = availableLangs.map(lang => {
		const langFromLists = find(landingLanguagesList, entity => entity.id == lang);
		return langFromLists.text;
	});

	const offerLangsWord = pluralize(offerLangs.length, ['язык', 'языка', 'языков']);

	return {
		offerLangs,
		offerLangsLen: offerLangs.length,
		offerLangsWord: intl(offerLangsWord)
	};
};

export const getLangsByTargetRelation = offer => {
	if (!offer || !offer.payouts || !offer.domains) return [];

	const flattenUniq = entity => uniq(flatten(entity));

	const locations = uniq(offer.payouts.map(payout => payout.country_code));

	const allGeoHasRelationsToLang = isEmpty(
		difference(locations, Object.keys(geoLandingLangRelation))
	);

	if (!allGeoHasRelationsToLang) {
		const missingGeoRelations = difference(locations, Object.keys(geoLandingLangRelation));
		// log missing geo
		missingGeoRelations.forEach(geo => log.add(offer.title, offer.id, geo));
		return flattenUniq(offer.domains.map(entity => entity.languages));
	}

	const locationsLangs = flattenUniq(locations.map(geo => geoLandingLangRelation[geo] || null).filter(geo => !!geo));

	return locationsLangs.filter(
		lang => find(offer.domains, domains => domains.languages.indexOf(lang) !== -1)
	);

};

export const isAccessGranted = item => item && item.available;
export const isWaitingForActivation = item => item && !item.available && item.offer_access_thread_id &&
!item.offer_access_thread_closed_at;
export const isAccessRejected = item => item && !item.available && item.offer_access_thread_id &&
item.offer_access_thread_closed_at;
export const showOfferBoostText = item => {
	let individualOrBoosted = item.goals.map(goal => {
		let flag = true;
		if (goal.is_individual_term || goal.boost_till !== null) {
			flag = false;
		}
		return flag;
	});
	return individualOrBoosted.indexOf(true) > -1;
};
export const wasBoostedLocation = (boost_info, offer_id, location_id) => {
	return boost_info && !!find(boost_info, {offer_id, location_id});
};
export const checkWasBoostedOffer = (boost_info, offer) => {
	return !some(offer.goals, goal => !find(boost_info, {offer_id: offer.id, location_id: goal.locationId}));
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/offers/shared.js